<template>
  <FormulateForm
    ref="contractForm"
    name="contractForm"
  >
    <e-sidebar
      id="sidebar-cost-contract-supplier"
      :title="edit ? $t('Editar Custo Contratado') : $t('Novo Custo Contratado')"
      :show="showSidebar"
      :fetching="fetching"
      :saving="saving"
      width="500px"
      @save="saveContract"
      @hidden="hideSidebar"
    >
      <template #content>
        <b-row>
          <b-col md="12">
            <e-store-combo
              id="cost-contract-supplier-store"
              v-model="contractForm.storeId"
              :required="false"
              :only-active-options="true"
              :select-single-option="false"
              :placeholder="$t('Geral')"
              :instruction="$t('Deixe em branco para um custo geral (todas as lojas)')"
            />
          </b-col>
          <b-col md="12">
            <e-search-sku
              id="cost-contract-supplier-sku"
              v-model="contractForm.skuId"
              :label="$t('Produto')"
              :required="true"
              :only-active="true"
              :include-fixed-assets="true"
              :default-options="skuDefaultOptions"
            />
          </b-col>
          <b-col md="12">
            <FormulateInput
              id="cost-contract-supplier-cost"
              v-model.number="contractForm.costValue"
              type="text-number"
              currency="R$"
              :precision="2"
              class="required"
              :label="$t('Custo')"
              validation="required|costGreaterThanZero"
              :validation-rules="{
                costGreaterThanZero: ({ value }) => Number(value) > 0,
              }"
              :validation-messages="{
                costGreaterThanZero: $t('O custo deve ser maior que zero'),
              }"
            />
          </b-col>
          <b-col md="12">
            <FormulateInput
              id="cost-contract-supplier-valid-from"
              v-model="contractForm.validFrom"
              type="datepicker"
              class="required"
              :label="$t('Vigência de')"
              :placeholder="$t('dd/mm/aaaa')"
              validation="required"
            />
          </b-col>
          <b-col md="12">
            <FormulateInput
              id="cost-contract-supplier-valid-to"
              v-model="contractForm.validTo"
              type="datepicker"
              :label="$t('Vigência até')"
              :placeholder="$t('dd/mm/aaaa')"
              :instruction="$t('Deixe em branco para vigência sem limite')"
              validation="validToAfterValidFrom"
              :validation-rules="{
                validToAfterValidFrom: ({ value }) =>
                  !value || !contractForm.validFrom || new Date(value) > new Date(contractForm.validFrom),
              }"
              :validation-messages="{
                validToAfterValidFrom: $t('A vigência até deve ser posterior à vigência de'),
              }"
            />
          </b-col>
        </b-row>
      </template>
    </e-sidebar>
  </FormulateForm>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import moment from 'moment'
import { ESidebar, ESearchSku } from '@/views/components'
import { formulateHelper } from '@/mixins'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import { mapActions } from 'vuex'

const getInitialForm = () => ({
  id: null,
  storeId: null,
  skuId: null,
  costValue: null,
  validFrom: null,
  validTo: null,
})

export default {
  components: {
    BRow,
    BCol,
    ESidebar,
    ESearchSku,
    EStoreCombo,
  },

  mixins: [formulateHelper],

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      edit: false,
      contractForm: getInitialForm(),
    }
  },

  computed: {
    // Opção pré-selecionada do ESearchSku (busca sob demanda não pré-carrega opções). Derivada do
    // formulário para ficar sempre em sincronia com o valor selecionado, garantindo que o combo
    // exiba o produto na edição.
    skuDefaultOptions() {
      const { skuId, skuName } = this.contractForm
      if (skuId == null) return []
      // id como string para casar com o value do combo (select-searchable compara com ===).
      return [{ id: String(skuId), name: skuName || `#${skuId}`, active: true }]
    },
  },

  methods: {
    ...mapActions('pages/catalog/costContractSuppliers', ['createContract', 'updateContract']),

    showCreate() {
      this.contractForm = getInitialForm()
      this.$refs.contractForm.hideErrors()
      this.edit = false
      this.showSidebar = true
    },

    show({ id, item } = {}) {
      if (!id && !item?.id) {
        this.showCreate()
      } else {
        this.contractForm = {
          ...getInitialForm(),
          ...(item || {}),
          id: id || item?.id,
          // Os combos comparam as opções por string; converte os ids na edição.
          // Loja em branco = Geral (null).
          storeId: item?.storeId != null ? String(item.storeId) : null,
          skuId: item?.skuId != null ? String(item.skuId) : null,
        }
        this.$refs.contractForm.hideErrors()
        this.edit = true
        this.showSidebar = true
      }
    },

    hideSidebar() {
      this.contractForm = getInitialForm()
      this.showSidebar = false
    },

    async saveContract() {
      this.$refs.contractForm.showErrors()
      if (this.$refs.contractForm.isValid) {
        try {
          this.saving = true

          const payload = {
            // Loja em branco ⇒ custo Geral (todas as lojas): envia null.
            storeId: this.contractForm.storeId || null,
            skuId: this.contractForm.skuId,
            costValue: this.contractForm.costValue,
            validFrom: this.contractForm.validFrom,
            // A "Vigência até" representa o dia inteiro selecionado: normaliza para
            // o fim do dia (23:59:59) em vez de 00:00. Vazio ⇒ vigência sem limite.
            validTo: this.contractForm.validTo
              ? moment(this.contractForm.validTo).endOf('day').format()
              : null,
          }

          if (this.edit) {
            await this.updateContract({ id: this.contractForm.id, payload })
          } else {
            await this.createContract(payload)
          }

          this.showSuccess({
            message: this.$t(`${this.edit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
          })
          this.showSidebar = false
          this.$emit('save')
        } catch (error) {
          this.showError({ error })
        } finally {
          this.saving = false
        }
      } else {
        this.showInvalidDataMessage()
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
